import { useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { Checkbox, FormControlLabel, useMediaQuery, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { schemas, useForm } from "helpers/FormHelpers";
import theme from "theme";
import { useAppSelector } from "redux/hooks";
import { selectIsSupport, selectMyUserType } from "redux/slices/session";
import LoadingSpinner from "components/LoadingSpinner";
import { P } from "theme/typography";
import { FormCheckboxList, FormTextField } from "helpers/FormHelpersComponents";
import { selectLenses, selectLensesStatus } from "redux/slices/client";
import RequestStatus from "redux/slices/RequestStatus";
import { Logo } from "components/Icons";

const CustomDialogContent = styled(DialogContent)({
  width: "400px",
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.only("xs")]: {
    width: "100%",
  },
}) as typeof DialogContent;

export interface AddUserDialogProps {
  open: boolean;
  onSubmit: (
    email: string,
    admin: boolean,
    support: boolean,
    lenses: number[],
    firstName?: string,
    lastName?: string
  ) => void;
  setOpen: (open: boolean) => void;
}
const AddUserDialog = (props: AddUserDialogProps) => {
  const form = useForm<"email" | "firstName" | "lastName" | "lenses">(
    {
      isValid: false,
      values: { email: "", firstName: "", lastName: "", lenses: [] },
      touched: { email: false, firstName: false, lastName: false, lenses: false },
      errors: {},
    },
    {
      email: schemas.email,
      lenses: schemas.nonEmptyArray,
      firstName: schemas.basicOptional,
      lastName: schemas.basicOptional,
    }
  );
  const [admin, setAdmin] = useState<boolean>(false);
  const [inviteAsSupport, setInviteAsSupport] = useState<boolean>(false);
  const myUserType = useAppSelector(selectMyUserType);
  const isSupport = useAppSelector(selectIsSupport);
  const formState = form.formStateHandle[0];
  const values = formState.values;
  const fullScreen = useMediaQuery(theme.breakpoints.only("xs"));
  const lensesStatus = useAppSelector(selectLensesStatus);
  const lenses = useAppSelector(selectLenses);

  const handleSubmit = () => {
    props.onSubmit(
      (values.email as string).trim(),
      admin,
      inviteAsSupport,
      (values.lenses as string[]).map((id) => parseInt(id)),
      values.firstName.trim(),
      values.lastName.trim()
    );
    props.setOpen(false);
  };

  return (
    <Dialog open={props.open} fullScreen={fullScreen}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
          borderBottom: `1px solid ${theme.palette.grey[100]}`,
        }}>
        <P variant="xlBold">Invite Pattern User</P>
        <Logo size={24} />
      </Box>

      <CustomDialogContent>
        <FormTextField label="Email" id="email" useForm={form} fullWidth sx={{ mt: 2, mb: 2 }} />
        <FormTextField label="First Name" id="firstName" useForm={form} fullWidth sx={{ mb: 2 }} />
        <FormTextField label="Last Name" id="lastName" useForm={form} fullWidth sx={{ mb: 2 }} />
        {lensesStatus === RequestStatus.PENDING && <LoadingSpinner />}
        {lensesStatus === RequestStatus.SUCCEEDED && (
          <FormCheckboxList
            items={lenses.map((l) => ({ value: `${l.id}`, name: l.name }))}
            id="lenses"
            label="Lens Access"
            useForm={form}
            sx={{ maxHeight: 135, overflow: "auto" }}
          />
        )}
        {myUserType === "admin" && (
          <div className="mt-4 flex flex-col gap-0">
            <p className="text-gray-300 text-xs font-semibold pb-1">Role</p>
            <FormControlLabel
              label="Invite as Admin"
              control={
                <Checkbox disabled={inviteAsSupport} checked={admin} onChange={(evt) => setAdmin(evt.target.checked)} />
              }
            />
            {isSupport && (
              <FormControlLabel
                label="Invite as Pattern Support"
                control={
                  <Checkbox
                    checked={inviteAsSupport}
                    onChange={(evt) => {
                      setAdmin(evt.target.checked);
                      setInviteAsSupport(evt.target.checked);
                    }}
                  />
                }
              />
            )}
          </div>
        )}
      </CustomDialogContent>
      <DialogActions>
        <Button onClick={() => props.setOpen(false)} color="default">
          Cancel
        </Button>
        <Button disabled={!formState.isValid} onClick={handleSubmit} variant="contained">
          Invite Pattern {inviteAsSupport ? "Support" : admin ? "Admin" : "Reviewer"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddUserDialog;
